// Customer portal site detail (/portal/sites/:id, Session AG Item 5; Session AFH tabbed redesign).
// One site's lifts with their full compliance detail (next service, LOLER status + certificate
// download, open items, recent visits), plus the history and document areas. The server scopes the
// site to the signed-in customer (404 if not theirs). A lapsed session (401) sends the customer
// back to sign in. NO pricing/contract value/engineer names.
//
// Above the tabs, always visible: the back link, the site name/address header, the signed service
// agreement download (Session ADZ) and the per-site services-on-time card. Below them, EIGHT sub-nav
// tabs (Session AFH's four-area history structure carried onto tabs, plus Training from AFL):
//   Lifts | Service History | Call-outs | Repairs and Works | Documents | Projects | Testing | Training
// The tab bar scrolls horizontally on a narrow screen; the history data loads once (a single
// siteHistory call) and the tabs only switch which panel is shown (no per-tab refetch). Training
// (Session AFL) is the one exception: it has its own endpoint and loads the first time its tab is
// opened, so the page's first paint is unchanged.
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PortalSite, PortalSiteHistory, PortalHistoryVisitRow, PortalHistoryWorkRow } from "../../lib/api";
import { PortalShell } from "./PortalShell";
import { PortalViewDocumentButton } from "./PortalDocumentViewer";
import { PortalLiftCard } from "./PortalLiftCard";
import { ShowAllList } from "./ShowAllList";
import { PortalTrainingPanel } from "./PortalTrainingPanel";
import { PortalSafedSection } from "./PortalSafedSection";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";

const TABS = [
  "Lifts",
  "Service History",
  "Call-outs",
  "Repairs and Works",
  "Documents",
  "Projects",
  "Testing",
  "Training",
] as const;
type Tab = (typeof TABS)[number];

export function PortalSiteDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState<PortalSite | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [history, setHistory] = useState<PortalSiteHistory | null>(null);
  const [historyError, setHistoryError] = useState(false);
  const [tab, setTab] = useState<Tab>("Lifts");

  useEffect(() => {
    let live = true;
    setState("loading");
    api.portal
      .site(id)
      .then((s) => {
        if (!live) return;
        setSite(s);
        setState("ready");
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else if (err instanceof ApiError && err.status === 404) setState("notfound");
        else setState("error");
      });
    // The history areas load separately (Session AFE); a failure never blanks the page,
    // it just shows the quiet fallback line in each history tab.
    api.portal
      .siteHistory(id)
      .then((h) => {
        if (!live) return;
        setHistory(h);
        setHistoryError(false);
      })
      .catch(() => {
        if (!live) return;
        setHistoryError(true);
      });
    return () => {
      live = false;
    };
  }, [id, navigate]);

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  if (state === "loading") return <FullScreenLoader />;

  return (
    <PortalShell wide onSignOut={logout}>
      <Link to="/portal/sites" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
        Back to your sites
      </Link>

      {state === "notfound" ? (
        <div className="mt-4 rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center text-ele-white">
          That site could not be found on your account.
        </div>
      ) : state === "error" || !site ? (
        <div className="mt-4 rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center text-ele-white">
          Something went wrong. Please try again in a moment, or call the office on{" "}
          <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
        </div>
      ) : (
        <>
          <header className="mb-6 mt-2">
            <h1 className="font-heading text-3xl tracking-wide text-ele-white">{site.name}</h1>
            {site.address && <p className="mt-1 text-sm text-ele-body">{site.address}</p>}
            {/* The signed service agreement (Session ADZ): shown only when the server says one is
                retrievable, served by the authenticated ownership-scoped proxy - the same pattern
                as the per-lift LOLER certificate download. */}
            {site.serviceAgreement && (
              <p className="mt-2">
                <a
                  href={api.portal.serviceAgreementHref(site.serviceAgreement.contractId)}
                  className="font-menu text-xs tracking-wide text-ele-green hover:underline"
                >
                  Download your service agreement (PDF)
                </a>
              </p>
            )}
          </header>

          {site.ppmCompliance && (
            // Plain uniform hairline (Session ACG: the coloured left-accent stripe is banned
            // app-wide; a standalone card gets a quiet border, no stripe).
            // Session BDH (D-BDH-4): the one on-time figure, as a percentage and its plain meaning only.
            <div className="mb-6 rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4">
              <p className="font-menu text-xs uppercase tracking-wider text-ele-body">Services on time (last 12 months)</p>
              {site.ppmCompliance.onTimePct === null ? (
                <p className="mt-1 text-base text-ele-body">Not enough history yet.</p>
              ) : (
                <p className="mt-1 font-heading text-3xl text-ele-loler">
                  {site.ppmCompliance.onTimePct}% <span className="text-base text-ele-body">of planned services were carried out on time</span>
                </p>
              )}
            </div>
          )}

          {/* Eight sub-nav tabs. The bar scrolls horizontally on a narrow screen; the active tab
              is a purple wash + white text (no underline border -- the app-wide no-accent-stripe
              rule). Default Lifts. */}
          <div data-hscroll role="tablist" aria-label="Site sections" className="mb-6 flex gap-2 overflow-x-auto pb-1">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={`shrink-0 whitespace-nowrap rounded-ele px-3 py-2 font-menu text-xs tracking-wide transition ${
                  tab === t ? "bg-ele-purple/20 text-ele-white" : "text-ele-body hover:text-ele-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div>
            {tab === "Lifts" && <LiftsPanel site={site} />}
            {tab === "Service History" && (
              <SiteSection title="Service history">
                <HistoryVisits kind="service" historyError={historyError} history={history} rows={history?.serviceVisits} empty="No completed service visits are recorded yet." />
              </SiteSection>
            )}
            {tab === "Call-outs" && (
              <SiteSection title="Call-out history">
                <HistoryVisits kind="callout" historyError={historyError} history={history} rows={history?.callouts} empty="No call-outs are recorded for this site." />
              </SiteSection>
            )}
            {tab === "Repairs and Works" && (
              <SiteSection title="Repairs and project works">
                <WorksPanel
                  historyError={historyError}
                  history={history}
                  rows={history?.works.filter((w) => w.source === "job")}
                  empty="No repair works are recorded for this site yet."
                />
              </SiteSection>
            )}
            {tab === "Documents" && (
              <SiteSection title="Site documents">
                <SiteDocuments site={site} />
              </SiteSection>
            )}
            {tab === "Projects" && (
              <SiteSection title="Project works">
                <WorksPanel
                  historyError={historyError}
                  history={history}
                  rows={history?.works.filter((w) => w.source === "project")}
                  empty="No project works are recorded for this site yet."
                />
              </SiteSection>
            )}
            {tab === "Testing" && (
              <SiteSection title="Supplementary testing">
                <PortalSafedSection siteId={id} />
                <TestingPanel historyError={historyError} history={history} />
              </SiteSection>
            )}
            {/* Session AFL: the training ELE has delivered to this site's staff, with each
                session's certificate. Loads on first open (its own endpoint). */}
            {tab === "Training" && (
              <SiteSection title="Training records">
                <PortalTrainingPanel siteId={site.id} />
              </SiteSection>
            )}
          </div>
        </>
      )}
    </PortalShell>
  );
}

// ---- Tab panels ------------------------------------------------------------

function LiftsPanel({ site }: { site: PortalSite }) {
  const lifts = site.lifts.filter((l) => !l.equipment);
  const equipment = site.lifts.filter((l) => l.equipment);
  return (
    <>
      {/* Session ACR: lifts and care equipment are grouped separately (same card, same compliance
          transparency); a lift-only site renders exactly as before. */}
      <h2 className="mb-3 font-heading text-xl text-ele-purple">
        {site.liftCount} {site.liftCount === 1 ? "lift" : "lifts"}
      </h2>
      {lifts.length === 0 ? (
        <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">No lifts are recorded at this site yet.</p>
      ) : (
        <div className="space-y-3">
          {lifts.map((lift) => (
            <PortalLiftCard key={lift.assetId} lift={lift} showLolerCert />
          ))}
        </div>
      )}

      {(site.equipmentCount ?? 0) > 0 && (
        <>
          {/* Session ACV: a gates-only contract's items are additional items, never
              "Care equipment" -- the heading follows what the group actually holds. */}
          <h2 className="mb-3 mt-6 font-heading text-xl text-ele-purple">
            {(() => {
              const heading = equipment.every((l) => l.additionalItem)
                ? "Covered items"
                : equipment.some((l) => l.additionalItem)
                  ? "Equipment and covered items"
                  : "Care equipment";
              return `${heading} (${site.equipmentCount})`;
            })()}
          </h2>
          <div className="space-y-3">
            {equipment.map((item) => (
              <PortalLiftCard key={item.assetId} lift={item} showLolerCert />
            ))}
          </div>
        </>
      )}
    </>
  );
}

function HistoryVisits({
  kind,
  historyError,
  history,
  rows,
  empty,
}: {
  kind: "service" | "callout";
  historyError: boolean;
  history: PortalSiteHistory | null;
  rows: PortalHistoryVisitRow[] | undefined;
  empty: string;
}) {
  if (historyError) return <HistoryUnavailable />;
  if (!history || !rows) return <HistoryLoading />;
  if (rows.length === 0) return <EmptyLine>{empty}</EmptyLine>;
  return <VisitList rows={rows} kind={kind} />;
}

function WorksPanel({
  historyError,
  history,
  rows,
  empty,
}: {
  historyError: boolean;
  history: PortalSiteHistory | null;
  rows: PortalHistoryWorkRow[] | undefined;
  empty: string;
}) {
  if (historyError) return <HistoryUnavailable />;
  if (!history || !rows) return <HistoryLoading />;
  if (rows.length === 0) return <EmptyLine>{empty}</EmptyLine>;
  return (
    <ShowAllList
      count={rows.length}
      render={(cap) => (
        <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
          {rows.slice(0, cap).map((w, i) => (
            <li key={i} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0">
              <div>
                <p className="text-sm text-ele-white">
                  {w.label}
                  {w.liftRef ? ` · ${w.liftRef}` : ""}
                </p>
                {w.title && <p className="text-xs text-ele-body">{w.title}</p>}
              </div>
              <span className="flex items-center gap-3 text-xs text-ele-body">
                {w.dateDisplay ?? "Completed"}
                {/* Session AMX: a job-sourced repair row opens the same certificate the office
                    downloads. A PROJECT row never carries one -- project documents are a different
                    family and out of scope -- so it shows nothing rather than a disabled control. */}
                {w.source === "job" &&
                  (w.reportAvailable && w.jobId ? (
                    <PortalViewDocumentButton
                      href={api.portal.jobReportHref(w.jobId)}
                      title={`${w.reportLabel ?? "Repair report"}, ${w.liftRef ?? "Lift"}, ${w.dateDisplay ?? "completed"}`}
                    />
                  ) : (
                    <span className="text-ele-body/70">Report being finalised.</span>
                  ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    />
  );
}

function SiteDocuments({ site }: { site: PortalSite }) {
  if ((site.siteDocuments?.length ?? 0) === 0) {
    return <EmptyLine>No site documents have been added yet.</EmptyLine>;
  }
  return (
    <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
      {site.siteDocuments!.map((doc) => (
        <li key={doc.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0">
          <div>
            <p className="text-sm text-ele-white">{doc.label}</p>
            <p className="text-xs text-ele-body">
              {doc.categoryLabel}
              {doc.validUntilDisplay ? ` · Valid to: ${doc.validUntilDisplay}` : ""}
            </p>
          </div>
          <PortalViewDocumentButton href={api.portal.siteDocumentHref(site.id, doc.id)} title={doc.label} />
        </li>
      ))}
    </ul>
  );
}

function TestingPanel({ historyError, history }: { historyError: boolean; history: PortalSiteHistory | null }) {
  return (
    <>
      <p className="mb-3 text-sm text-ele-body">
        Supplementary tests to SAFed guidelines are carried out when instructed by the inspection body, alongside
        the regular thorough examinations shown on each lift.
      </p>
      {historyError ? (
        <HistoryUnavailable />
      ) : !history ? (
        <HistoryLoading />
      ) : (
        <>
          {history.supplementaryTests.length === 0 ? (
            <EmptyLine>No supplementary test records yet.</EmptyLine>
          ) : (
            <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
              {history.supplementaryTests.map((t, i) => (
                <li key={i} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0">
                  <p className="text-sm text-ele-white">
                    {t.liftRef} · {t.testType}
                  </p>
                  <span className="text-xs text-ele-body">
                    {t.lastTestDisplay ? `Last test: ${t.lastTestDisplay}` : "Not yet carried out"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {history.supplementaryAnnexes.length > 0 && (
            <div className="mt-3">
              <h3 className="mb-2 font-menu text-xs uppercase tracking-wider text-ele-body">Test annex documents</h3>
              <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
                {history.supplementaryAnnexes.map((doc) => (
                  <li key={doc.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0">
                    <p className="text-sm text-ele-white">{doc.label}</p>
                    <PortalViewDocumentButton href={api.portal.companyDocumentHref(doc.id)} title={doc.label} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ---- Small shared bits -----------------------------------------------------

function SiteSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 font-heading text-xl text-ele-purple">{title}</h2>
      {children}
    </section>
  );
}

function EmptyLine({ children }: { children: ReactNode }) {
  return <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">{children}</p>;
}

function HistoryLoading() {
  return <p className="text-sm text-ele-body">Loading...</p>;
}

function HistoryUnavailable() {
  return <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">This section could not be loaded. Please refresh to try again.</p>;
}

// Session AMX: which proxy URL opens a per-site row's report. Identical rule to the cross-site
// feed's: the row's own reportSource decides, so a job's completion certificate and a call-out's
// filed attendance report can never be served from the wrong route.
function visitReportHref(v: PortalHistoryVisitRow): string | null {
  if (!v.reportAvailable) return null;
  if (v.reportSource === "callout") return v.calloutId ? api.portal.calloutReportHref(v.calloutId) : null;
  return v.jobId ? api.portal.jobReportHref(v.jobId) : null;
}

function VisitList({ rows, kind }: { rows: PortalHistoryVisitRow[]; kind: "service" | "callout" }) {
  return (
    <ShowAllList
      count={rows.length}
      render={(cap) => (
        <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
          {rows.slice(0, cap).map((v, i) => {
            const href = visitReportHref(v);
            return (
              <li key={i} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0">
                <p className="text-sm text-ele-white">{v.liftRef}</p>
                <span className="flex items-center gap-3 text-xs text-ele-body">
                  {v.outcome && <span className="text-ele-green">{v.outcome}</span>}
                  {v.dateDisplay}
                  {href ? (
                    <PortalViewDocumentButton
                      href={href}
                      title={`${kind === "service" ? "Service record" : "Call-out report"}, ${v.liftRef}, ${v.dateDisplay}`}
                    />
                  ) : (
                    <span className="text-ele-body/70">Report being finalised.</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    />
  );
}
